import { setShowOverlay, setMessages } from "../store/actions";
import store from "../store/store";
import * as wss from "./wss";
import Peer from "simple-peer";
import { fetchTURNCredentials, getTurnIceServers } from "./turn";

const defaultConstraints = {
  audio: true,
  video: {
    width: { ideal: 640, max: 1280 },
    height: { ideal: 480, max: 720 },
    frameRate: { ideal: 30, max: 30 },
  },
};

const onlyAudioConstraints = {
  audio: true,
  video: false,
};

let localStream;
let remoteStreams = [];
let peers = {};
let streams = [];
let peerCheckInterval;

export const getLocalPreviewAndInitRoomConnection = async (
  isRoomHost,
  identity,
  roomId = null,
  onlyAudio
) => {
  await fetchTURNCredentials();
  const constraints = onlyAudio ? onlyAudioConstraints : defaultConstraints;

  navigator.mediaDevices
    .getUserMedia(constraints)
    .then((stream) => {
      console.log("Successfully received local stream");
      localStream = stream;

      // Initially disable audio and video - user can toggle them on using buttons
      if (localStream.getAudioTracks().length > 0) {
        localStream.getAudioTracks()[0].enabled = false;
      }
      if (localStream.getVideoTracks().length > 0) {
        localStream.getVideoTracks()[0].enabled = false;
      }

      showLocalVideoPreview(localStream);

      store.dispatch(setShowOverlay(false));

      if (isRoomHost) {
        wss.createNewRoom(identity, onlyAudio, roomId);
      } else {
        wss.joinRoom(identity, roomId, onlyAudio);
      }

      // startInactivePeerCleanup(); // Start periodic cleanup
    })
    .catch((err) => {
      console.error("Error accessing local stream", err);

      // Provide more detailed error messages
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        alert("Camera/Microphone access denied. Please allow camera and microphone permissions in your browser settings and refresh the page.");
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        alert("No camera or microphone found. Please connect a camera/microphone and refresh the page.");
      } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
        alert("Camera or microphone is already in use by another application. Please close other apps using camera/microphone.");
      } else {
        alert("Error accessing camera/microphone: " + err.message);
      }
    });
};

const getConfiguration = () => {
  const turnIceServers = getTurnIceServers();

  return {
    iceServers: [
      { urls: "stun:stun.l.google.com:19302" },
      { urls: "stun:stun1.l.google.com:19302" },
      { urls: "stun:stun2.l.google.com:19302" },
      { urls: "stun:stun3.l.google.com:19302" },
      { urls: "stun:stun4.l.google.com:19302" },
      { urls: "stun:stun.stunprotocol.org:3478" },
      ...(turnIceServers || []),
    ],
    iceCandidatePoolSize: 10,
  };
};

const messengerChannel = "messenger";

export const prepareNewPeerConnection = (connUserSocketId, isInitiator) => {
  const configuration = getConfiguration();

  console.log("Preparing peer connection:", { connUserSocketId, isInitiator, hasLocalStream: !!localStream });

  // Check if local stream is available
  if (!localStream) {
    console.error("Cannot create peer connection - no local stream available!");
    // Retry after a short delay to allow stream to initialize
    setTimeout(() => {
      if (localStream) {
        prepareNewPeerConnection(connUserSocketId, isInitiator);
      }
    }, 1000);
    return;
  }

  peers[connUserSocketId] = new Peer({
    initiator: isInitiator,
    config: configuration,
    stream: localStream,
    channelName: messengerChannel,
    trickle: true,
  });

  peers[connUserSocketId].on("signal", (data) => {
    console.log("Sending signaling data to:", connUserSocketId);
    wss.signalPeerData({
      signal: data,
      connUserSocketId,
    });
  });

  peers[connUserSocketId].on("stream", (stream) => {
    console.log("Received remote stream from:", connUserSocketId, "Tracks:", stream.getTracks().length);
    addStream(stream, connUserSocketId);
    streams.push(stream);
    remoteStreams.push(stream);
  });

  peers[connUserSocketId].on("data", (data) => {
    appendNewMessage(JSON.parse(data));
  });

  peers[connUserSocketId].on("close", () => {
    console.log("Peer connection closed for:", connUserSocketId);
    removePeerConnection({ socketId: connUserSocketId });
  });

  peers[connUserSocketId].on("error", (err) => {
    console.error("Peer connection error for", connUserSocketId, ":", err);
    removePeerConnection({ socketId: connUserSocketId });
  });

  // Handle peer connection state changes
  peers[connUserSocketId].on("connect", () => {
    console.log("Peer connection established with:", connUserSocketId);
  });
};

export const handleSignalingData = (data) => {
  const { connUserSocketId, signal } = data;

  if (peers[connUserSocketId]) {
    console.log("Handling signaling data from:", connUserSocketId);
    try {
      peers[connUserSocketId].signal(signal);
    } catch (err) {
      console.error("Error signaling peer", connUserSocketId, ":", err);
    }
  } else {
    console.warn("Received signaling data for unknown peer:", connUserSocketId);
    // Try to create a new peer connection if one doesn't exist
    // This handles the case where the initial connection setup might have failed
  }
};

export const removePeerConnection = (data) => {
  const socketId = data.socketId;

  const videoContainer = document.getElementById(socketId);
  const videoEl = document.getElementById(`${socketId}-video`);

  if (videoContainer && videoEl) {
    const tracks = videoEl.srcObject?.getTracks() || [];
    tracks.forEach((t) => t.stop());
    videoEl.srcObject = null;
    videoContainer.removeChild(videoEl);
    videoContainer.parentNode?.removeChild(videoContainer);
  }

  if (peers[socketId]) {
    try {
      peers[socketId].destroy();
      delete peers[socketId];
    } catch (err) {
      console.error(`Failed to destroy peer ${socketId}:`, err);
    }
  }

  remoteStreams = remoteStreams.filter(
    (stream) => !stream.getTracks().some((track) => track.id === socketId)
  );
};

export const cleanupAllConnections = () => {
  Object.keys(peers).forEach((socketId) => {
    removePeerConnection({ socketId });
  });

  if (localStream) {
    localStream.getTracks().forEach((track) => track.stop());
    localStream = null;
  }

  const videosContainer = document.getElementById("videos_portal");
  if (videosContainer) videosContainer.innerHTML = "";

  peers = {};
  streams = [];
  remoteStreams = [];

  store.dispatch(setMessages([]));
  store.dispatch(setShowOverlay(true));

  checkAndCleanupRoomId();

  //stopInactivePeerCleanup();
};

const checkAndCleanupRoomId = () => {
  const roomId = localStorage.getItem("roomId");
  const connectedPeers = Object.keys(peers || {});
  if (!roomId || connectedPeers.length === 0) {
    // console.log("No active connections. Removing roomId...");
    localStorage.removeItem("roomId");
    // console.log("After removal, roomId:", localStorage.getItem("roomId")); // Should be `null`
  } else {
    // console.log("Active connections found. RoomId is retained.");
  }
};


const showLocalVideoPreview = (stream) => {
  const videosContainer = document.getElementById("videos_portal");
  if (!videosContainer) {
    console.error("Videos portal not found!");
    return;
  }

  console.log("Showing local video preview, tracks:", stream.getTracks().length);

  videosContainer.classList.add("videos_portal_styles");

  const videoContainer = document.createElement("div");
  videoContainer.className = "video_track_container local_video";
  videoContainer.id = "local-video-container";

  const videoElement = document.createElement("video");
  videoElement.autoplay = true;
  videoElement.muted = true; // Local video should be muted
  videoElement.playsInline = true; // Important for mobile
  videoElement.loop = false;
  videoElement.srcObject = stream;
  videoElement.style.cssText = "width: 100%; height: 100%; object-fit: cover; background: #000;";
  videoElement.id = "local-video-element";

  videoElement.onloadedmetadata = () => {
    console.log("Local video metadata loaded");
    videoElement.play().catch((err) => console.warn("Local video playback failed:", err));
  };

  // Additional play attempt for mobile browsers
  setTimeout(() => {
    if (videoElement.paused) {
      videoElement.play().catch(err => console.warn("Local video delayed play failed:", err));
    }
  }, 500);

  // Try one more time after a longer delay
  setTimeout(() => {
    if (videoElement.paused) {
      videoElement.play().catch(err => console.warn("Local video final play attempt failed:", err));
    }
  }, 2000);

  // Add participant name label
  const identity = store.getState().identity;
  const user = store.getState().user;
  const photoURL = user?.photoURL || null;

  const nameLabel = document.createElement("div");
  nameLabel.className = "participant_name_label";
  nameLabel.style.cssText = "position: absolute; bottom: 8px; left: 8px; background: rgba(0,0,0,0.7); color: white; padding: 4px 10px; border-radius: 4px; font-size: 12px; font-weight: 500; z-index: 10;";
  nameLabel.innerHTML = `${identity} `;

  // Create video off placeholder with user name
  const videoOffPlaceholder = document.createElement("div");
  videoOffPlaceholder.id = "local-video-placeholder";
  videoOffPlaceholder.style.cssText = "display: none; position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: #2a2a2a; flex-direction: column; justify-content: center; align-items: center; z-index: 5;";

  // Show profile image if available, otherwise show initials
  if (photoURL) {
    const avatarImg = document.createElement("img");
    avatarImg.src = photoURL;
    avatarImg.style.cssText = "width: 80px; height: 80px; border-radius: 50%; object-fit: cover; margin-bottom: 12px; border: 3px solid #4a90d9;";
    avatarImg.onerror = function () {
      // Fallback to initials if image fails to load
      this.style.display = 'none';
      const avatarCircle = document.createElement("div");
      avatarCircle.style.cssText = "width: 80px; height: 80px; border-radius: 50%; background: #4a90d9; display: flex; justify-content: center; align-items: center; margin-bottom: 12px;";
      avatarCircle.innerHTML = `<span style="color: white; font-size: 36px; font-weight: 600;">${identity.charAt(0).toUpperCase()}</span>`;
      videoOffPlaceholder.insertBefore(avatarCircle, videoOffPlaceholder.firstChild);
    };
    videoOffPlaceholder.appendChild(avatarImg);
  } else {
    const avatarCircle = document.createElement("div");
    avatarCircle.style.cssText = "width: 80px; height: 80px; border-radius: 50%; background: #4a90d9; display: flex; justify-content: center; align-items: center; margin-bottom: 12px;";
    avatarCircle.innerHTML = `<span style="color: white; font-size: 36px; font-weight: 600;">${identity.charAt(0).toUpperCase()}</span>`;
    videoOffPlaceholder.appendChild(avatarCircle);
  }

  const nameDisplay = document.createElement("div");
  nameDisplay.style.cssText = "color: white; font-size: 16px; font-weight: 500; text-align: center;";
  nameDisplay.innerHTML = `${identity} `;

  videoOffPlaceholder.appendChild(nameDisplay);

  // Check initial state - since video is now enabled by default, show video
  const videoTrack = stream.getVideoTracks()[0];
  const isVideoEnabled = videoTrack && videoTrack.enabled;

  if (!isVideoEnabled) {
    // Video is OFF - show placeholder
    videoElement.style.display = "none";
    videoOffPlaceholder.style.display = "flex";
    nameLabel.style.display = "none";
  } else {
    // Video is ON - show video element
    videoElement.style.display = "block";
    videoOffPlaceholder.style.display = "none";
    nameLabel.style.display = "block";
  }

  videoContainer.appendChild(videoElement);
  videoContainer.appendChild(videoOffPlaceholder);
  videoContainer.appendChild(nameLabel);

  if (store.getState().connectOnlyWithAudio) {
    videoContainer.appendChild(getAudioOnlyLabel());
  }

  videosContainer.appendChild(videoContainer);
};

const addStream = (stream, connUserSocketId) => {
  const videosContainer = document.getElementById("videos_portal");
  if (!videosContainer) {
    console.error("Videos container not found!");
    return;
  }

  console.log("Adding stream for user:", connUserSocketId, "Track count:", stream.getTracks().length);

  // Check if we already have a video element for this user
  const existingContainer = document.getElementById(connUserSocketId);
  if (existingContainer) {
    // Update existing video stream
    const existingVideo = existingContainer.querySelector("video");
    if (existingVideo) {
      existingVideo.srcObject = stream;
      existingVideo.play().catch(err => console.warn("Failed to play remote video:", err));
    }
    return;
  }

  const videoContainer = document.createElement("div");
  videoContainer.id = connUserSocketId;
  videoContainer.className = "remote_video_container video_track_container";

  const videoElement = document.createElement("video");
  videoElement.autoplay = true;
  videoElement.muted = false; // Remote videos should not be muted
  videoElement.playsInline = true; // Important for mobile
  videoElement.loop = false;
  videoElement.srcObject = stream;
  videoElement.id = `${connUserSocketId}-video`;
  videoElement.style.cssText = "width: 100%; height: 100%; object-fit: cover; background: #000;";

  // Handle video loadedmetadata for better mobile support
  videoElement.onloadedmetadata = () => {
    console.log("Remote video metadata loaded for:", connUserSocketId);
    videoElement.play().catch((err) =>
      console.warn("Remote video playback failed:", err)
    );
  };

  // Additional play attempts for mobile browsers
  const attemptPlay = () => {
    if (videoElement.paused) {
      videoElement.play()
        .then(() => console.log("Remote video playing for:", connUserSocketId))
        .catch(err => {
          console.warn("Delayed play failed for", connUserSocketId, ":", err);
          // Try again after another delay
          setTimeout(() => {
            if (videoElement.paused) {
              videoElement.play().catch(e => console.warn("Final play attempt failed:", e));
            }
          }, 2000);
        });
    }
  };

  setTimeout(attemptPlay, 500);
  setTimeout(attemptPlay, 1500);
  setTimeout(attemptPlay, 3000);

  videoElement.addEventListener("click", () => {
    videoElement.classList.toggle("full_screen");
  });

  videoContainer.appendChild(videoElement);

  const participants = store.getState().participants;
  const participant = participants.find(
    (p) => p.socketId === connUserSocketId
  );

  if (participant) {
    if (participant?.onlyAudio) {
      // Show placeholder with profile image for audio-only participants
      const placeholder = document.createElement("div");
      placeholder.style.cssText = "position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: #2a2a2a; display: flex; flex-direction: column; justify-content: center; align-items: center; z-index: 5;";

      if (participant.photoURL) {
        const avatarImg = document.createElement("img");
        avatarImg.src = participant.photoURL;
        avatarImg.style.cssText = "width: 80px; height: 80px; border-radius: 50%; object-fit: cover; margin-bottom: 12px; border: 3px solid #4a90d9;";
        avatarImg.onerror = function () {
          this.style.display = 'none';
          const avatarCircle = document.createElement("div");
          avatarCircle.style.cssText = "width: 80px; height: 80px; border-radius: 50%; background: #4a90d9; display: flex; justify-content: center; align-items: center; margin-bottom: 12px;";
          avatarCircle.innerHTML = `<span style="color: white; font-size: 36px; font-weight: 600;">${participant.identity.charAt(0).toUpperCase()}</span>`;
          placeholder.insertBefore(avatarCircle, placeholder.firstChild);
        };
        placeholder.appendChild(avatarImg);
      } else {
        const avatarCircle = document.createElement("div");
        avatarCircle.style.cssText = "width: 80px; height: 80px; border-radius: 50%; background: #4a90d9; display: flex; justify-content: center; align-items: center; margin-bottom: 12px;";
        avatarCircle.innerHTML = `<span style="color: white; font-size: 36px; font-weight: 600;">${participant.identity.charAt(0).toUpperCase()}</span>`;
        placeholder.appendChild(avatarCircle);
      }

      const nameDisplay = document.createElement("div");
      nameDisplay.style.cssText = "color: white; font-size: 16px; font-weight: 500; text-align: center;";
      nameDisplay.textContent = participant.identity;
      placeholder.appendChild(nameDisplay);

      videoContainer.appendChild(placeholder);
      videoContainer.appendChild(getAudioOnlyLabel(participant.identity));
    } else {
      // Add participant name label for video participants
      const nameLabel = document.createElement("div");
      nameLabel.className = "participant_name_label";
      nameLabel.style.cssText = "position: absolute; bottom: 8px; left: 8px; background: rgba(0,0,0,0.7); color: white; padding: 4px 10px; border-radius: 4px; font-size: 12px; font-weight: 500; z-index: 10;";
      nameLabel.textContent = participant.identity;
      videoContainer.appendChild(nameLabel);
    }
  }

  videosContainer.appendChild(videoContainer);
};

const getAudioOnlyLabel = (identity = "") => {
  const labelContainer = document.createElement("div");
  labelContainer.classList.add("label_only_audio_container");

  const label = document.createElement("p");
  label.classList.add("label_only_audio_text");
  label.innerHTML = `Only audio ${identity}`;

  labelContainer.appendChild(label);
  return labelContainer;
};

export const toggleMic = (isMuted) => {
  localStream.getAudioTracks()[0].enabled = !isMuted;
};

// Force mute (used when host mutes participant)
export const forceMute = (mute) => {
  if (localStream && localStream.getAudioTracks().length > 0) {
    localStream.getAudioTracks()[0].enabled = !mute;
  }
};

export const toggleCamera = (isDisabled) => {
  // Update the video track enabled state
  localStream.getVideoTracks()[0].enabled = !isDisabled;

  // Update the UI to show/hide video or placeholder
  const videoElement = document.getElementById("local-video-element");
  const videoPlaceholder = document.getElementById("local-video-placeholder");
  const videoContainer = document.getElementById("local-video-container");
  const nameLabel = videoContainer?.querySelector(".participant_name_label");

  if (videoElement && videoPlaceholder) {
    if (isDisabled) {
      // Video is now OFF - show placeholder with user name
      videoElement.style.display = "none";
      videoPlaceholder.style.display = "flex";
      // Hide name label when video is off
      if (nameLabel) {
        nameLabel.style.display = "none";
      }
    } else {
      // Video is now ON - show video
      videoElement.style.display = "block";
      videoPlaceholder.style.display = "none";
      // Show name label when video is on
      if (nameLabel) {
        nameLabel.style.display = "block";
      }
    }
  }
};

export const toggleScreenShare = (
  isScreenSharingActive,
  screenSharingStream = null
) => {
  switchVideoTracks(isScreenSharingActive ? localStream : screenSharingStream);
};

const switchVideoTracks = (stream) => {
  for (let socketId in peers) {
    const peer = peers[socketId];
    const oldTracks = peer.streams[0].getTracks();
    const newTracks = stream.getTracks();

    oldTracks.forEach((oldTrack) => {
      const replacementTrack = newTracks.find(
        (newTrack) => newTrack.kind === oldTrack.kind
      );
      if (replacementTrack) {
        peer.replaceTrack(oldTrack, replacementTrack, peer.streams[0]);
      }
    });
  }
};

const appendNewMessage = (messageData) => {
  const messages = store.getState().messages;
  store.dispatch(setMessages([...messages, messageData]));
};

export const sendMessageUsingDataChannel = (messageContent) => {
  const identity = store.getState().identity;

  const localMessageData = {
    content: messageContent,
    identity,
    messageCreatedByMe: true,
  };

  appendNewMessage(localMessageData);

  const messageData = {
    content: messageContent,
    identity,
  };

  const stringifiedMessageData = JSON.stringify(messageData);
  Object.values(peers).forEach((peer) => peer.send(stringifiedMessageData));
};

const startInactivePeerCleanup = () => {
  if (peerCheckInterval) clearInterval(peerCheckInterval);

  peerCheckInterval = setInterval(() => {
    Object.entries(peers).forEach(([socketId, peer]) => {
      if (peer.destroyed || peer._destroyed || peer._connected === false) {
        removePeerConnection({ socketId });
      }
    });
  }, 5000);
};

const stopInactivePeerCleanup = () => {
  if (peerCheckInterval) {
    clearInterval(peerCheckInterval);
    peerCheckInterval = null;
  }
};

export const getLocalStream = () => localStream;
export const getRemoteStreams = () => remoteStreams;
export const getPeers = () => peers;

// Debug function to check connection status
export const checkConnectionStatus = () => {
  console.log("=== Connection Status ===");
  console.log("Local stream:", localStream ? "Available" : "Not available");
  console.log("Local stream tracks:", localStream?.getTracks().length);
  console.log("Peer connections:", Object.keys(peers).length);

  Object.keys(peers).forEach((peerId) => {
    const peer = peers[peerId];
    console.log(`Peer ${peerId}:`, {
      connected: peer.connected,
      initiator: peer.initiator,
      streams: peer.streams?.length || 0,
      destroyed: peer.destroyed
    });
  });

  console.log("Remote streams:", remoteStreams.length);
  console.log("========================");
};

// Update video container to show screen share mode
export const setScreenShareVideoContainer = (socketId, isScreenSharing) => {
  let container;

  if (socketId === "local" || socketId === "local-video-container") {
    container = document.getElementById("local-video-container");
  } else {
    container = document.getElementById(socketId);
  }

  if (container) {
    if (isScreenSharing) {
      container.classList.add("screen-share-active");
      // Add screen share label
      const existingLabel = container.querySelector(".screen-share-label");
      if (!existingLabel) {
        const label = document.createElement("div");
        label.className = "screen-share-label";
        label.textContent = "Screen Sharing";
        container.appendChild(label);
      }
    } else {
      container.classList.remove("screen-share-active");
      // Remove screen share label
      const label = container.querySelector(".screen-share-label");
      if (label) {
        label.remove();
      }
    }
  }
};
